import { getAPI } from 'services/api/apiAsync';
import type { AppDispatch, RootState } from 'app/store';
import { API_ENDPOINTS } from 'utils/constants';
import { setChildItems } from './childItemsSlice';
import { selectChildItemsLoaded } from './childItemsSelectors';
import type { ChildMonitoredItem } from './types';

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// GET /api/client/monitored_items/dependents/children → per-child
// enrollment (each entry: { id: childUserId, monitoredItems: […] }).
// Mirrors Angular's `retryWhen` behaviour (3 attempts, 500ms between
// each). Errors are swallowed after all retries so a failed
// dependent-items fetch does not block the whole plan-members page.
//
// Guarded by `selectChildItemsLoaded` for the fetch-once rule (CLAUDE.md
// Rule 8). The consumer `usePlanMembersInit` also caches at the hook
// level so this guard is a belt-and-braces safety net.
//
// Pass `force: true` to bypass the loaded guard — used by dark-web
// child mode after an add/remove mutation to resync
// `state.childItems.items[childId]` with the server. Matches Angular's
// `getChildMonitoredItems()` call inside `watchlist-item-card.ts:840,876`
// after `newSaveMonitoredItems` / `removeMonitoredItemWithCallbackStatus`.
function fetchChildItems(force = false) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!force && selectChildItemsLoaded(getState())) return;

    let lastError: unknown;
    // Sequential retry with 500ms delay — Angular parity with `retryWhen`.
    // `no-await-in-loop` deliberately disabled: we want serial retries,
    // not parallel requests.
    /* eslint-disable no-await-in-loop */
    for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt += 1) {
      try {
        const response = await dispatch(
          getAPI({ url: API_ENDPOINTS.invitorChildItems }),
        ).unwrap();
        const list = Array.isArray(response)
          ? (response as ChildMonitoredItem[])
          : [];
        dispatch(setChildItems(list));
        return;
      } catch (error) {
        lastError = error;
        if (attempt < RETRY_ATTEMPTS) {
          await delay(RETRY_DELAY_MS);
        }
      }
    }
    /* eslint-enable no-await-in-loop */

    // All retries exhausted — settle the loaded flag with an empty list so
    // the page can render its normal empty state instead of spinning.
    dispatch(setChildItems([]));
    // Retain the last error for future logging hooks without rejecting —
    // Angular's implementation also silently absorbs the terminal error.
    if (lastError) {
      // eslint-disable-next-line no-console
      console.warn('[child-items] fetch failed after retries:', lastError);
    }
  };
}

export { fetchChildItems };
export default fetchChildItems;
